import {
  FieldType,
  IMappedStringDefinitionInput,
  ITableColumnFieldsFragment,
} from '../../../../utils/apollo/graphqlSchema';
import React, { useCallback } from 'react';
import produce from 'immer';
import {
  CustomColumnCell,
  CustomDefinitionFormWrapper,
  MappedStringRow,
} from './DerivedColumnEditUI';
import { FieldLabel, InputField } from '../../../../components/Form';
import { DataModelerSelectTableColumn } from '../DataModelerFieldDropdown';
import { CenteredDiv } from '../DataModelerCommonUI';
import {
  SquareAddButton,
  SquareRemoveButton,
} from '../../../../components/Button';

interface IProps {
  input: IMappedStringDefinitionInput;
  setInput: (input: IMappedStringDefinitionInput) => void;
  viewKey: string;
  tableKey: string;
  availableColumnsFromServer: ITableColumnFieldsFragment[];
}

interface IEditMappedStringModel {
  setMappedColumn: (column: ITableColumnFieldsFragment) => void;
  setOriginal: (rowIndex: number, v: string) => void;
  setMapped: (rowIndex: number, v: string) => void;
  removePair: (rowIndex: number) => void;
  addEmptyPair: () => void;
  setMapNull: (v: string) => void;
  setMapElse: (v: string) => void;
}

const useEditMappedStringModel = ({
  input,
  setInput,
}: {
  input: IMappedStringDefinitionInput;
  setInput: (input: IMappedStringDefinitionInput) => void;
}): IEditMappedStringModel => {
  const setMappedColumn = useCallback(
    (column: ITableColumnFieldsFragment) => {
      setInput({
        ...input,
        mappedColumn: column.column,
      });
    },
    [input, setInput],
  );

  const setOriginal = useCallback(
    (rowIndex: number, v: string) => {
      if (rowIndex < 0 || rowIndex >= input.values.length) {
        return;
      }
      const values = produce(input.values, (draft) => {
        draft[rowIndex].original = v;
      });
      setInput({
        ...input,
        values,
      });
    },
    [input, setInput],
  );

  const setMapped = useCallback(
    (rowIndex: number, v: string) => {
      if (rowIndex < 0 || rowIndex >= input.values.length) {
        return;
      }
      const values = produce(input.values, (draft) => {
        draft[rowIndex].mapped = v;
      });
      setInput({
        ...input,
        values,
      });
    },
    [input, setInput],
  );

  const removePair = useCallback(
    (rowIndex: number) => {
      if (rowIndex < 0 || rowIndex >= input.values.length) {
        return;
      }
      const values = produce(input.values, (draft) => {
        draft.splice(rowIndex, 1);
      });
      setInput({
        ...input,
        values,
      });
    },
    [input, setInput],
  );

  const addEmptyPair = useCallback(() => {
    const values = produce(input.values, (draft) => {
      draft.push({ original: '', mapped: '' });
    });
    setInput({
      ...input,
      values,
    });
  }, [input, setInput]);

  const setMapNull = useCallback(
    (v: string) => {
      const mapNull = v ? v : null;
      setInput({
        ...input,
        mapNull,
      });
    },
    [input, setInput],
  );

  const setMapElse = useCallback(
    (v: string) => {
      const mapElse = v ? v : null;
      setInput({
        ...input,
        mapElse,
      });
    },
    [input, setInput],
  );

  return {
    setMappedColumn,
    setOriginal,
    setMapped,
    removePair,
    addEmptyPair,
    setMapNull,
    setMapElse,
  };
};

const DerivedColumnMappedStringEdit = ({
  input,
  setInput,
  viewKey,
  tableKey,
  availableColumnsFromServer,
}: IProps) => {
  const {
    setMappedColumn,
    setOriginal,
    setMapped,
    removePair,
    addEmptyPair,
    setMapNull,
    setMapElse,
  } = useEditMappedStringModel({ input, setInput });

  const mapElsePlaceholder = input.mappedColumn
    ? input.mappedColumn
    : '(optional)';

  return (
    <>
      <CustomColumnCell>
        <FieldLabel>Select column to map</FieldLabel>
        <DataModelerSelectTableColumn
          viewKey={viewKey}
          tableKey={tableKey}
          availableColumnsFromServer={availableColumnsFromServer}
          columnName={input.mappedColumn}
          setColumn={setMappedColumn}
          addFieldOnSetColumn={false}
          customWidth={240}
          fieldTypeFilter={[FieldType.String]}
        />
      </CustomColumnCell>
      <CustomDefinitionFormWrapper>
        <MappedStringRow>
          <div>
            <FieldLabel>Db Value</FieldLabel>
          </div>
          <div />
          <div>
            <FieldLabel>Result Value</FieldLabel>
          </div>
          <div />
        </MappedStringRow>
        {input.values.map(({ original, mapped }, ix) => {
          return (
            <MappedStringRow key={`row-${ix}`}>
              <div>
                <InputField
                  autoComplete="off"
                  name="draftOriginal"
                  value={original}
                  onChange={(v) => setOriginal(ix, v)}
                  placeholder={'Enter db value'}
                />
              </div>
              <CenteredDiv>to</CenteredDiv>
              <div>
                <InputField
                  autoComplete="off"
                  name="draftMapped"
                  value={mapped}
                  onChange={(v) => setMapped(ix, v)}
                  placeholder={'Enter result value'}
                />
              </div>
              {ix === input.values.length - 1 ? (
                <SquareAddButton onClick={addEmptyPair} content={'Add'} />
              ) : (
                <SquareRemoveButton
                  onClick={() => removePair(ix)}
                  content={'Remove'}
                  disabled={input.values.length === 1}
                />
              )}
            </MappedStringRow>
          );
        })}
        <MappedStringRow>
          <div>NULL</div>
          <CenteredDiv>to</CenteredDiv>
          <div>
            <InputField
              autoComplete="off"
              name="search"
              value={input.mapNull ?? ''}
              onChange={setMapNull}
              placeholder={'(NULL)'}
            />
          </div>
          <div />
        </MappedStringRow>
        <MappedStringRow>
          <div>ELSE</div>
          <CenteredDiv>to</CenteredDiv>
          <div>
            <InputField
              autoComplete="off"
              name="mapElse"
              value={input.mapElse ?? ''}
              onChange={setMapElse}
              placeholder={mapElsePlaceholder}
            />
          </div>
          <div />
        </MappedStringRow>
      </CustomDefinitionFormWrapper>
    </>
  );
};

export default DerivedColumnMappedStringEdit;
